/**
 * English — the source catalogue.
 *
 * Every other locale is typed as `Catalogue`, so this file decides the shape:
 * add a key here and the build breaks until all five other languages define it
 * too. That is deliberate. The alternative — a missing key falling back to
 * English at runtime — means a customer somewhere sees half a translated page
 * and nobody finds out.
 *
 * Notes for translators:
 *   - `{{name}}` placeholders must survive translation, spelling intact.
 *   - Entries with `one`/`other` are plurals. Add whatever forms your language
 *     needs (`zero`, `two`, `few`, `many`); Intl picks the right one.
 *   - `zero` is optional everywhere and is about READING WELL, not grammar:
 *     use it when "0 items" would sound wrong.
 */
export const en = {
    common: {
        save: 'Save',
        saving: 'Saving…',
        saved: 'Saved',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        close: 'Close',
        back: 'Back',
        retry: 'Retry',
        loading: 'Loading…',
        search: 'Search',
        signOut: 'Sign out',
        store: 'Store',
        status: 'Status',
        email: 'Email',
        password: 'Password',
        firstName: 'First name',
        lastName: 'Last name',
        phone: 'Phone',
        total: 'Total',
        subtotal: 'Subtotal',
        quantity: 'Quantity',
        price: 'Price',
        stock: 'Stock',
        language: 'Language',
        never: 'Never',
        none: '—',
    },
    nav: {
        products: 'Products',
        orders: 'Orders',
        customers: 'Customers',
        settings: 'Settings',
        website: 'Website',
        apps: 'Apps',
        themes: 'Themes',
        installs: 'Installations',
    },
    auth: {
        signInTitle: 'Sign in',
        signInSubtitle: 'Sign in to your store admin.',
        partnerSubtitle: 'Build apps and themes for merchants.',
        signIn: 'Sign in',
        signingIn: 'Signing in…',
        createStore: 'Start your store',
        creatingStore: 'Creating your store…',
        haveAccount: 'Already have a store? Sign in',
        needAccount: 'No store yet? Create one',
        storeName: 'Store name',
        storeAddress: 'Store address',
        passwordHint: 'At least 12 characters',
        slugChecking: 'Checking availability…',
        slugTaken: '{{slug}} is taken. We suggest {{suggestion}}.',
        slugFree: '{{slug}} is available.',
    },
    products: {
        title: 'Products',
        searchPlaceholder: 'Search by title, handle or vendor…',
        newProduct: 'New product',
        columnProduct: 'Product',
        columnPrice: 'Price',
        columnStock: 'Stock',
        columnVariants: 'Variants',
        statusActive: 'Active',
        statusDraft: 'Draft',
        statusArchived: 'Archived',
        empty: 'No products yet. Add your first one to start selling.',
        variantCount: { one: '{{count}} variant', other: '{{count}} variants' },
        stockLevel: {
            zero: 'Out of stock',
            one: '{{count}} in stock',
            other: '{{count}} in stock',
        },
        variantTitle: 'Variant title',
        sku: 'SKU',
        couldNotLoadVariants: 'Could not load variants.',
    },
    orders: {
        title: 'Orders',
        searchPlaceholder: 'Search by order number or email…',
        columnOrder: 'Order',
        columnCustomer: 'Customer',
        columnPayment: 'Payment',
        columnFulfilment: 'Fulfilment',
        columnPlaced: 'Placed',
        shipTo: 'Ship to',
        item: 'Item',
        qty: 'Qty',
        unit: 'Unit',
        empty: 'No orders yet.',
        itemCount: { one: '{{count}} item', other: '{{count}} items' },
        financial: {
            PENDING: 'Payment pending',
            AUTHORIZED: 'Authorised',
            PAID: 'Paid',
            PARTIALLY_REFUNDED: 'Partially refunded',
            REFUNDED: 'Refunded',
            VOIDED: 'Voided',
        },
        fulfilment: {
            UNFULFILLED: 'Unfulfilled',
            PARTIAL: 'Partially fulfilled',
            FULFILLED: 'Fulfilled',
            RESTOCKED: 'Restocked',
        },
    },
    customers: {
        title: 'Customers',
        searchPlaceholder: 'Search by name or email…',
        newCustomer: 'New customer',
        columnCustomer: 'Customer',
        columnOrders: 'Orders',
        columnMarketing: 'Marketing',
        lifetimeSpend: 'Lifetime spend',
        acceptsMarketing: 'Accepts marketing',
        empty: 'No customers yet.',
        orderCount: { zero: 'No orders', one: '{{count}} order', other: '{{count}} orders' },
    },
    settings: {
        title: 'Store settings',
        storefront: 'Your storefront',
        storefrontHint: 'This is live now. In local development the subdomain is not in DNS — the storefront dev server pins the Host header instead.',
        details: 'Details',
        customDomain: 'Custom domain',
        customDomainHint: 'Point a CNAME at Upweo, then enter the bare hostname. Leave blank to use the address above.',
        addressHint: '{{host}} — changing this breaks existing links',
        currency: 'Currency',
        country: 'Country',
        plan: 'Plan',
        currencyLocked: 'Currency cannot be changed after orders exist — historical totals would become meaningless.',
        languages: 'Languages',
        defaultLanguage: 'Default language',
        defaultLanguageHint: 'What a shopper sees when their browser asks for a language you do not offer.',
        storefrontLanguages: 'Storefront languages',
        storefrontLanguagesHint: 'Shoppers can switch between the languages you tick. Untick a language to hide it rather than show a half-translated shop.',
        saveFailed: 'Could not save settings',
    },
    payments: {
        title: 'Payments',
        testMode: 'Test mode',
        live: 'Live',
        needsWebhook: 'Needs webhook',
        notConnected: 'Not connected',
        account: 'Account',
        secretKey: 'Secret key',
        publishableKey: 'Publishable key',
        webhookSecret: 'Webhook signing secret',
        verified: 'Verified',
        webhookEndpoint: 'Webhook endpoint',
        connect: 'Connect Stripe',
        update: 'Update keys',
        disconnect: 'Disconnect',
        verifying: 'Verifying…',
        savedAndVerified: 'Saved and verified',
        copy: 'Copy',
        copied: 'Copied',
        disabledNotConnected: 'Checkout is disabled until a Stripe account is connected. Shoppers can browse and build a cart, but cannot pay.',
        disabledNoWebhook: 'Your keys are saved, but checkout stays off until a webhook signing secret is added. Without it we can start a payment but never learn that it succeeded — orders would sit unpaid while the money arrives in Stripe.',
        secretKeyHintNew: 'Starts with sk_ or rk_. Stored encrypted and never shown again.',
        secretKeyHintExisting: 'Leave blank to keep the key you already saved.',
        publishableKeyHint: 'Starts with pk_. Sent to the shopper’s browser, so this one is public by design.',
        webhookSecretHint: 'Starts with whsec_. Shown once in Stripe when you create the endpoint above.',
        saveFailed: 'Could not save payment settings',
        disconnectFailed: 'Could not disconnect',
    },
    shop: {
        shopAll: 'Shop all',
        searchPlaceholder: 'Search…',
        soldOut: 'Sold out',
        addToCart: 'Add to cart',
        adding: 'Adding…',
        options: 'Options',
        productNotFound: 'Product not found',
        continueShopping: 'Continue shopping',
        poweredBy: '{{store}} — powered by Upweo',
        cart: 'Cart',
        cartEmpty: 'Your cart is empty',
        decreaseQuantity: 'Decrease quantity',
        increaseQuantity: 'Increase quantity',
        remove: 'Remove',
        taxNotice: 'Tax and shipping are not calculated yet.',
        checkout: 'Checkout',
        contact: 'Contact',
        shippingAddress: 'Shipping address',
        address: 'Address',
        city: 'City',
        postcode: 'Postcode',
        country: 'Country',
        orderSummary: 'Order summary',
        editCart: 'Edit cart',
        continueToPayment: 'Continue to payment · {{total}}',
        preparingPayment: 'Preparing payment…',
        payment: 'Payment',
        pay: 'Pay {{total}}',
        paying: 'Paying…',
        paymentSecurity: 'Payments are processed by Stripe. Your card details are never sent to this store.',
        paymentFailed: 'Payment failed. Try another card.',
        thankYou: 'Thank you',
        orderPaid: 'Order {{order}} for {{total}} is paid.',
        confirmationSent: 'A confirmation is on its way to {{email}}.',
        cartCount: { zero: 'Cart', one: 'Cart ({{count}})', other: 'Cart ({{count}})' },
    },
    partner: {
        title: 'Upweo Partners',
        apps: 'Apps',
        newApp: 'New app',
        clientId: 'Client ID',
        clientSecret: 'Client secret',
        scopes: 'Scopes',
        requestedScopes: 'Requested scopes',
        installs: 'Installations',
        grantedScopes: 'Granted scopes',
        installed: 'Installed',
        themes: 'Themes',
        noThemes: 'No themes yet.',
        noApps: 'No apps yet.',
        activePartnerAccount: 'Active partner account',
        installCount: { zero: 'Not installed', one: '{{count}} install', other: '{{count}} installs' },
    },
    /**
     * The public marketing site at the root domain.
     *
     * These are DEFAULTS. Every key here can be overridden per language from the
     * admin panel (SiteContent), which is what makes the site editable without a
     * deploy. Shipping the copy in code as well means a fresh database still
     * renders a complete site, and a language nobody has rewritten still reads
     * correctly instead of showing gaps.
     *
     * Feature and FAQ slots are a fixed set. A slot whose title is blanked out in
     * the admin panel is hidden, which is how staff add and remove cards without
     * a schema change.
     */
    website: {
        subtitle: 'The public site at upweo.com',
        copy: 'Marketing copy',
        copyHint: 'Leave a field empty to use the wording Upweo ships. What you see greyed out is the current default for this language.',
        clearHint: 'Clearing a field restores the default.',
        pricing: 'Pricing plans',
        pricingHint: 'Prices are shown to visitors in their own number format. Only the wording is translated — the figure is the same everywhere.',
        published: 'Live',
        hidden: 'Hidden',
        publish: 'Publish',
        hide: 'Hide',
        popular: 'Most popular',
    },
    site: {
        nav: {
            features: 'Features',
            pricing: 'Pricing',
            partners: 'Partners',
            signIn: 'Sign in',
            startFree: 'Start free',
        },
        hero: {
            title: 'Everything you need to sell online',
            subtitle: 'Upweo gives you a storefront, a checkout and the tools to run the business behind them — in your language, in your currency, on your own domain.',
            ctaPrimary: 'Create your store',
            ctaSecondary: 'See pricing',
            note: 'No card required to start.',
        },
        features: {
            heading: 'Built for real shops',
            subheading: 'The parts of an online business that are tedious to build, already done.',
            f1Title: 'Your own storefront',
            f1Body: 'Every store gets its own address the moment you sign up, and can move to your own domain whenever you are ready.',
            f2Title: 'Products and stock',
            f2Body: 'Variants with their own prices and SKUs, stock tracked per location, and inventory reserved the moment an order is placed.',
            f3Title: 'Real payments',
            f3Body: 'Connect your own Stripe account. Your customers’ money goes directly to you — we are never in the middle of it.',
            f4Title: 'Speaks your customers’ language',
            f4Body: 'Six languages, right-to-left support, and prices formatted the way each country actually writes them. Receipts arrive in the language the shopper was reading.',
            f5Title: 'Orders and customers',
            f5Body: 'Every order snapshotted at the moment of purchase, so your history stays accurate no matter how the catalogue changes later.',
            f6Title: 'Apps and themes',
            f6Body: 'A partner ecosystem with OAuth, scoped permissions and webhooks, so developers can extend your store without touching your data.',
        },
        pricing: {
            heading: 'Simple pricing',
            subheading: 'Start free. Move up when the orders do.',
            perMonth: '/month',
            cta: 'Get started',
            popular: 'Most popular',
            placeholderNotice: 'These figures are placeholders while Upweo is in beta. Nothing is charged today.',
            empty: 'Pricing is being finalised. Creating a store is free in the meantime.',
        },
        partners: {
            heading: 'Build for Upweo',
            subheading: 'Make apps and themes for merchants, with a proper OAuth flow, scoped permissions and webhooks.',
            cta: 'Become a partner',
            signIn: 'Partner sign in',
            b1Title: 'Real APIs',
            b1Body: 'A public GraphQL storefront API and a REST admin API, both versioned.',
            b2Title: 'Scoped access',
            b2Body: 'Merchants grant exactly the permissions your app asks for, and can revoke them.',
            b3Title: 'Webhooks',
            b3Body: 'Subscribe to the events you care about, with signed, retried delivery.',
        },
        signup: {
            heading: 'Create your store',
            subheading: 'It takes about a minute. You can change any of this later.',
            submit: 'Create my store',
            submitting: 'Creating your store…',
            haveAccount: 'Already selling with Upweo? Sign in',
            success: 'Your store is ready. Taking you to your dashboard…',
        },
        partnerSignup: {
            heading: 'Become a partner',
            subheading: 'Tell us who you are. We review new partners before your apps go public.',
            accountName: 'Business or developer name',
            submit: 'Create partner account',
            submitting: 'Creating your account…',
            success: 'Your partner account is created and pending review. Taking you to the portal…',
            haveAccount: 'Already a partner? Sign in',
            existingUserHint: 'Already have an Upweo account? Use the same email and password and we will add partner access to it.',
        },
        footer: {
            tagline: 'Multi-tenant commerce, built in the open.',
            product: 'Product',
            forPartners: 'For partners',
            rights: '© {{year}} Upweo',
        },
    },
    /**
     * Errors, keyed by the API's machine-readable `code`.
     *
     * The server sends a code AND an English message; the client prefers this
     * translation and falls back to the server's prose. That split is what makes
     * a localised API possible without the server knowing who is asking — the
     * generic codes translate, and anything specific enough to need server
     * context still reads correctly, just in English.
     */
    errors: {
        BAD_REQUEST: 'That request could not be understood.',
        VALIDATION_FAILED: 'Please check the highlighted fields.',
        UNAUTHENTICATED: 'Please sign in again.',
        FORBIDDEN: 'You do not have permission to do that.',
        TENANT_NOT_RESOLVED: 'We could not work out which store this is.',
        NOT_FOUND: 'That could not be found.',
        CONFLICT: 'That conflicts with something that already exists.',
        RATE_LIMITED: 'Too many attempts. Please wait a moment.',
        PAYMENT_FAILED: 'The payment could not be completed.',
        INTERNAL_ERROR: 'Something went wrong on our side.',
        network: 'Could not reach the server. Check your connection.',
    },
    email: {
        orderConfirmation: {
            subject: 'Order {{order}} confirmed',
            preheader: 'We have received your order and payment.',
            heading: 'Thank you for your order',
            greeting: 'Hello {{name}},',
            intro: '{{store}} has received your order and your payment has cleared.',
            orderLabel: 'Order',
            datePlaced: 'Placed',
            itemsHeading: 'What you ordered',
            subtotal: 'Subtotal',
            shipping: 'Shipping',
            tax: 'Tax',
            total: 'Total',
            shippingTo: 'Shipping to',
            nextSteps: 'We will email you again when your order ships.',
            footer: 'This email was sent by {{store}}.',
        },
        passwordReset: {
            subject: 'Reset your Upweo password',
            preheader: 'A link to choose a new password.',
            heading: 'Reset your password',
            intro: 'Someone asked to reset the password for {{email}}.',
            action: 'Choose a new password',
            expiry: 'This link works once and expires in {{minutes}} minutes.',
            ignore: 'If this was not you, you can ignore this email — your password has not changed.',
            footer: 'Sent by Upweo.',
        },
    },
};
//# sourceMappingURL=en.js.map